import React, { useEffect, useRef, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { ChatMessage } from '../types';

interface ChatPanelProps {
  messages: ChatMessage[];
  currentUserId: string;
  currentUserRole: 'admin' | 'teacher' | 'student' | 'parent';
  onSendMessage: (message: string, isPrivate: boolean) => boolean | Promise<boolean>;
  allowPrivate?: boolean;
  isOpen: boolean;
  onClose: () => void;
  mode?: 'drawer' | 'overlay' | 'inline';
  className?: string;
}

const ChatPanel = ({
  messages,
  currentUserId,
  currentUserRole,
  onSendMessage,
  allowPrivate = false,
  isOpen,
  onClose,
  mode = 'drawer',
  className,
}: ChatPanelProps) => {
  const [newMessage, setNewMessage] = useState('');
  const [isPrivateMode, setIsPrivateMode] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async () => {
    if (newMessage.trim()) {
      const ok = await Promise.resolve(onSendMessage(newMessage, isPrivateMode));
      if (ok) setNewMessage('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSendMessage();
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' });
  };

  const roleLabel = (role: ChatMessage['senderRole']) => {
    switch (role) {
      case 'admin':
        return 'Admin';
      case 'teacher':
        return 'Profesor';
      case 'student':
        return 'Elev';
      case 'parent':
        return 'Părinte';
      default:
        return role;
    }
  };

  if (!isOpen) return null;

  const content = (
    <div className={`w-full h-full bg-card border border-border shadow-lg rounded-lg flex flex-col ${className || ''}`}>
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center gap-2">
          <Icon name="MessageSquare" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Chatul clasei</h3>
        </div>
        <button
          onClick={onClose}
          className="p-2 rounded-md hover:bg-muted transition-colors duration-150"
          aria-label="Închide chatul"
        >
          <Icon name="X" size={20} className="text-muted-foreground" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-4">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <Icon name="MessageCircle" size={48} className="text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">Nu există mesaje încă</p>
            <p className="text-xs text-muted-foreground mt-1">Începe conversația!</p>
          </div>
        ) : (
          messages.map((message) => {
            const isOwnMessage = message.senderId === currentUserId;
            return (
              <div key={message.id} className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[80%] ${
                    isOwnMessage ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground'
                  } rounded-xl p-4`}
                >
                  {!isOwnMessage && (
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-medium">{message.senderName}</span>
                      <span
                        className={`text-xs px-1.5 py-0.5 rounded ${
                          message.senderRole === 'teacher' || message.senderRole === 'admin'
                            ? 'bg-accent text-accent-foreground'
                            : 'bg-secondary text-secondary-foreground'
                        }`}
                      >
                        {roleLabel(message.senderRole)}
                      </span>
                    </div>
                  )}
                  <p className="text-sm whitespace-pre-wrap break-words leading-relaxed">{message.message}</p>
                  <div className="flex items-center justify-between mt-2">
                    <span className={`text-xs ${isOwnMessage ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                      {formatTime(message.timestamp)}
                    </span>
                    {message.isPrivate && (
                      <span className="text-xs flex items-center gap-1">
                        <Icon name="Lock" size={10} />
                        Privat
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="p-5 border-t border-border bg-muted/30">
        {allowPrivate && (currentUserRole === 'teacher' || currentUserRole === 'admin') && (
          <div className="mb-3">
            <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
              <input
                type="checkbox"
                checked={isPrivateMode}
                onChange={(e) => setIsPrivateMode(e.target.checked)}
                className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
              />
              <Icon name="Lock" size={14} className="text-muted-foreground" />
              <span>Trimite ca mesaj privat</span>
            </label>
          </div>
        )}

        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Input
              type="text"
              placeholder="Scrie mesajul..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              className="resize-none"
            />
          </div>
          <Button
            variant="default"
            size="icon"
            onClick={() => void handleSendMessage()}
            disabled={!newMessage.trim()}
            iconName="Send"
            aria-label="Trimite mesajul"
          />
        </div>

        <p className="text-xs text-muted-foreground mt-3">Apasă Enter pentru a trimite, Shift+Enter pentru rând nou</p>
      </div>
    </div>
  );

  if (mode === 'overlay') {
    return (
      <div className="fixed inset-0 z-[1200] bg-black/40 flex items-end lg:items-center justify-center p-3">
        <div className="w-full max-w-3xl h-[70vh] lg:h-[75vh] animate-scale-in">{content}</div>
      </div>
    );
  }

  if (mode === 'inline') {
    return <div className="w-full h-full">{content}</div>;
  }

  return (
    <div className="fixed right-0 top-16 bottom-0 w-80 z-[950] animate-slide-in-right lg:w-96">
      {content}
    </div>
  );
};

export default ChatPanel;
